import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { TipCampanie } from '@b4l/shared'
import { api } from '../../../lib/api'
import { Modal } from '../../ui/Modal'
import { DocumenteContainer } from '../DocumenteContainer'
import { eroare } from './comun'

/** Starea finala a fiecarui tip: evenimentul „participat", restul „incheiata". */
function stareFinala(tip: TipCampanie, anulare: boolean): string {
  if (tip === 'eveniment') return anulare ? 'anulat' : 'participat'
  return anulare ? 'anulata' : 'incheiata'
}

const MIN_RAPORT = 20

/**
 * Închiderea unei activități, cu raport obligatoriu (Madalin, 15.10.2026: „să fie necesar să dai
 * câteva detalii și să poți atașa eventual poze sau documente"). Anularea cere doar motivul.
 *
 * Regula e și pe server: fără raport, închiderea e refuzată — și din „Editează”.
 */
export function InchideActivitatea({
  campanieId,
  tip,
  titlu,
  anulare,
  avertisment,
  onInchide,
}: {
  campanieId: number
  tip: TipCampanie
  titlu: string
  anulare: boolean
  /** Ce mai e deschis (marfă netrimisă, task-uri) — se spune, nu blochează. */
  avertisment?: string | null
  onInchide: () => void
}) {
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const lungime = text.trim().length
  const destul = anulare ? lungime >= 3 : lungime >= MIN_RAPORT

  const inchide = useMutation({
    mutationFn: async () =>
      (
        await api.patch(`/campanii/${campanieId}`, {
          status: stareFinala(tip, anulare),
          motiv: text.trim(),
        })
      ).data,
    onSuccess: () => {
      toast.success(anulare ? 'Activitatea a fost anulată.' : 'Activitatea a fost încheiată.')
      void qc.invalidateQueries({ queryKey: ['campanie', campanieId] })
      void qc.invalidateQueries({ queryKey: ['campanii'] })
      onInchide()
    },
    onError: eroare,
  })

  return (
    <Modal
      open
      onClose={onInchide}
      size="lg"
      title={anulare ? `Anulezi „${titlu}”?` : `Închizi „${titlu}”`}
      footer={
        <>
          <button
            type="button"
            onClick={onInchide}
            className="rounded-caseta px-3 py-1.5 text-corp text-b4l-text-muted hover:bg-b4l-bg"
          >
            Renunță
          </button>
          <button
            type="button"
            disabled={!destul || inchide.isPending}
            onClick={() => inchide.mutate()}
            className={`rounded-caseta px-3.5 py-1.5 text-corp font-semibold text-white hover:opacity-90 disabled:opacity-40 ${
              anulare ? 'bg-b4l-red' : 'bg-b4l-green'
            }`}
          >
            {inchide.isPending
              ? 'Se salvează…'
              : anulare
                ? 'Anulează activitatea'
                : 'Închide cu raport'}
          </button>
        </>
      }
    >
      <div className="grid gap-3">
        {avertisment && (
          <p className="rounded-lg bg-b4l-amber-light px-3 py-2 text-mic text-b4l-amber-dark">
            {avertisment}
          </p>
        )}
        <label className="grid gap-1">
          <span className="text-mic font-semibold text-b4l-text">
            {anulare ? 'De ce se anulează' : 'Raportul'}
          </span>
          <textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={anulare ? 3 : 6}
            maxLength={5000}
            placeholder={
              anulare
                ? 'ex.: organizatorul a amânat congresul pentru primăvară'
                : 'Cum a fost: câți oameni, ce contacte, ce s-a dat, ce a mers și ce facem altfel data viitoare'
            }
            className="rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-2 text-corp"
          />
          {!anulare && (
            <span className={`text-mic ${destul ? 'text-b4l-text-dim' : 'text-b4l-text-muted'}`}>
              {destul
                ? 'Rămâne în istoricul activității și pe Prezentare.'
                : `Câteva rânduri — încă ${MIN_RAPORT - lungime} caractere.`}
            </span>
          )}
        </label>
        {!anulare && (
          <div className="grid gap-1.5">
            <span className="text-mic font-semibold text-b4l-text">
              Poze sau documente <span className="font-normal text-b4l-text-dim">(opțional)</span>
            </span>
            <DocumenteContainer tip="campanie" parinteId={campanieId} canWrite />
          </div>
        )}
      </div>
    </Modal>
  )
}
